import { useEffect, useState } from "react";
import { Link } from "react-router";
import { usePageTitle } from "../../hooks/usePageTitle";

interface Customer {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  oauth_provider: string | null;
  phone: string | null;
  created_at: string;
  last_login: string | null;
  status: "active" | "inactive";
  total_orders?: number;
  total_spent?: number;
}

interface CustomerDetails extends Customer {
  orders: any[];
  messages: any[];
  stats: {
    totalOrders: number;
    totalSpent: number;
    averageOrderValue: number;
  };
}

export default function Users() {
  usePageTitle("User Management");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [authFilter, setAuthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchCustomers();
  }, [page, searchQuery, authFilter, statusFilter]);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (searchQuery) params.append("search", searchQuery);
      if (authFilter) params.append("authMethod", authFilter);
      if (statusFilter) params.append("status", statusFilter);

      const response = await fetch(`/api/admin/users?${params}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data.users || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId: number) => {
    setDetailsLoading(true);
    try {
      const response = await fetch(`/api/admin/users/${customerId}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedCustomer(data);
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const updateCustomerStatus = async (customerId: number, status: "active" | "inactive") => {
    try {
      const response = await fetch(`/api/admin/users/${customerId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        // Refresh customer list
        fetchCustomers();
        // Update selected customer if viewing details
        if (selectedCustomer && selectedCustomer.id === customerId) {
          fetchCustomerDetails(customerId);
        }
      }
    } catch (error) {
      console.error("Error updating customer status:", error);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getAuthMethodBadge = (provider: string | null) => {
    if (!provider) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">Email</span>;
    }
    if (provider === "google") {
      return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Google</span>;
    }
    if (provider === "facebook") {
      return <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">Facebook</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">{provider}</span>;
  };

  const getStatusBadge = (status: string) => {
    return status === "active" ? (
      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Active</span>
    ) : (
      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Inactive</span>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">User Management</h1>
        <div className="text-sm text-gray-600">
          {total} total customers
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6 p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name or email..."
              className="input w-full"
            />
          </div>

          {/* Auth Method Filter */}
          <div>
            <label htmlFor="authFilter" className="block text-sm font-medium mb-1">
              Auth Method
            </label>
            <select
              id="authFilter"
              value={authFilter}
              onChange={(e) => {
                setAuthFilter(e.target.value);
                setPage(1);
              }}
              className="input w-full"
            >
              <option value="">All Methods</option>
              <option value="email">Email/Password</option>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="statusFilter" className="block text-sm font-medium mb-1">
              Status
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="input w-full"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="card p-5">
        {loading ? (
          <div className="text-center py-12">Loading customers...</div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            No customers found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Customer</th>
                    <th className="text-left py-3 px-2">Email</th>
                    <th className="text-left py-3 px-2">Auth Method</th>
                    <th className="text-left py-3 px-2">Orders</th>
                    <th className="text-left py-3 px-2">Total Spent</th>
                    <th className="text-left py-3 px-2">Joined</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-left py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          {customer.avatar_url ? (
                            <img
                              src={customer.avatar_url}
                              alt={`${customer.first_name} ${customer.last_name}`}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs font-bold text-gray-600">
                                {customer.first_name.charAt(0)}{customer.last_name.charAt(0)}
                              </span>
                            </div>
                          )}
                          <span className="font-medium">
                            {customer.first_name} {customer.last_name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-gray-600">{customer.email}</td>
                      <td className="py-3 px-2">{getAuthMethodBadge(customer.oauth_provider)}</td>
                      <td className="py-3 px-2">{customer.total_orders || 0}</td>
                      <td className="py-3 px-2 font-medium">
                        {formatCurrency(customer.total_spent || 0)}
                      </td>
                      <td className="py-3 px-2 text-gray-600">{formatDate(customer.created_at)}</td>
                      <td className="py-3 px-2">{getStatusBadge(customer.status)}</td>
                      <td className="py-3 px-2">
                        <button
                          onClick={() => fetchCustomerDetails(customer.id)}
                          className="text-sm text-black underline hover:no-underline"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Customer Details Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  {selectedCustomer.avatar_url ? (
                    <img
                      src={selectedCustomer.avatar_url}
                      alt={`${selectedCustomer.first_name} ${selectedCustomer.last_name}`}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-2xl font-bold text-gray-600">
                        {selectedCustomer.first_name.charAt(0)}{selectedCustomer.last_name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold">
                      {selectedCustomer.first_name} {selectedCustomer.last_name}
                    </h2>
                    <p className="text-gray-600">{selectedCustomer.email}</p>
                    {selectedCustomer.phone && (
                      <p className="text-sm text-gray-600">{selectedCustomer.phone}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-gray-500 hover:text-black text-2xl"
                >
                  ×
                </button>
              </div>

              {detailsLoading ? (
                <div className="text-center py-12">Loading details...</div>
              ) : (
                <>
                  {/* Customer Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="card bg-gray-50">
                      <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                      <p className="text-2xl font-bold">{selectedCustomer.stats.totalOrders}</p>
                    </div>
                    <div className="card bg-gray-50">
                      <p className="text-sm text-gray-600 mb-1">Total Spent</p>
                      <p className="text-2xl font-bold">{formatCurrency(selectedCustomer.stats.totalSpent)}</p>
                    </div>
                    <div className="card bg-gray-50">
                      <p className="text-sm text-gray-600 mb-1">Avg Order Value</p>
                      <p className="text-2xl font-bold">{formatCurrency(selectedCustomer.stats.averageOrderValue)}</p>
                    </div>
                  </div>

                  {/* Account Info */}
                  <div className="card mb-6">
                    <h3 className="font-bold mb-4">Account Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Auth Method</p>
                        <p className="font-medium">{selectedCustomer.oauth_provider || "Email/Password"}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Status</p>
                        <p className="font-medium">{selectedCustomer.status}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Member Since</p>
                        <p className="font-medium">{formatDate(selectedCustomer.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Last Login</p>
                        <p className="font-medium">{formatDate(selectedCustomer.last_login)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mb-6">
                    {selectedCustomer.status === "active" ? (
                      <button
                        onClick={() => updateCustomerStatus(selectedCustomer.id, "inactive")}
                        className="btn-secondary bg-red-50 text-red-700 hover:bg-red-100"
                      >
                        Deactivate Account
                      </button>
                    ) : (
                      <button
                        onClick={() => updateCustomerStatus(selectedCustomer.id, "active")}
                        className="btn-primary"
                      >
                        Reactivate Account
                      </button>
                    )}
                  </div>

                  {/* Recent Orders */}
                  <div className="mb-6">
                    <h3 className="font-bold mb-3">Recent Orders</h3>
                    {selectedCustomer.orders.length === 0 ? (
                      <p className="text-gray-600 text-sm">No orders yet</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedCustomer.orders.slice(0, 5).map((order: any) => (
                          <Link
                            key={order.id}
                            to={`/admin/orders`}
                            className="block p-3 border border-gray-200 rounded hover:bg-gray-50"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{order.order_number}</span>
                              <span>{formatCurrency(order.total)}</span>
                            </div>
                            <p className="text-sm text-gray-600">{formatDate(order.created_at)}</p>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Contact Messages */}
                  <div>
                    <h3 className="font-bold mb-3">Support Tickets</h3>
                    {selectedCustomer.messages.length === 0 ? (
                      <p className="text-gray-600 text-sm">No messages yet</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedCustomer.messages.slice(0, 5).map((message: any) => (
                          <Link
                            key={message.id}
                            to={`/admin/contact-messages`}
                            className="block p-3 border border-gray-200 rounded hover:bg-gray-50"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{message.subject}</span>
                              <span className={`px-2 py-1 text-xs rounded ${
                                message.status === "replied"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {message.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{formatDate(message.created_at)}</p>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
